import {
  DEFAULT_OPTION_COLOR,
  OPTION_COLOR_KEYS,
  type OptionColorKey,
  isOptionColorKey,
} from '@common/constants/optionColors'
import { CustomFieldNumberFormat, CustomFieldType } from '@common/interfaces/customField'

export const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  [CustomFieldType.Text]: 'Text',
  [CustomFieldType.Number]: 'Number',
  [CustomFieldType.SingleSelect]: 'Single-select',
  [CustomFieldType.MultiSelect]: 'Multi-select',
  [CustomFieldType.Date]: 'Date',
  [CustomFieldType.People]: 'People',
  [CustomFieldType.GitHubPullRequests]: 'GitHub Pull Requests',
}

// Shown in the task panel only, never as a project table column.
export const isPanelOnlyCustomField = ({ type }: { type: CustomFieldType }): boolean =>
  type === CustomFieldType.GitHubPullRequests

export const CUSTOM_FIELD_NUMBER_FORMAT_LABELS: Record<CustomFieldNumberFormat, string> = {
  [CustomFieldNumberFormat.Plain]: 'Plain',
  [CustomFieldNumberFormat.Currency]: 'Currency',
  [CustomFieldNumberFormat.Percentage]: 'Percentage',
}

// The shared option-colour palette (Asana-style) — used by BOTH custom-field
// options and task-project swatches. There is deliberately only one colour set:
// keys come from OPTION_COLOR_KEYS in common/ (the server assigns one when a
// project is created without a colour), and the labels plus the CSS-var helpers
// below are presentation, so they stay client-side. The colours themselves live
// as theme-aware `--cf-color-<key>-bg`/`-text`/`-solid` tokens in tokens.scss
// (light + dark) — the `cf-` prefix predates the project use and is just a name.
// Adding a colour means adding a key in common/ and its tokens; never a second
// palette.
export interface IOptionColor {
  key: string
  label: string
}

const OPTION_COLOR_LABELS: Record<OptionColorKey, string> = {
  red: 'Red',
  orange: 'Orange',
  'yellow-orange': 'Amber',
  yellow: 'Yellow',
  'yellow-green': 'Lime',
  green: 'Green',
  'blue-green': 'Teal',
  aqua: 'Aqua',
  blue: 'Blue',
  indigo: 'Indigo',
  purple: 'Purple',
  magenta: 'Magenta',
  'hot-pink': 'Pink',
  'cool-gray': 'Gray',
}

export const OPTION_COLORS: IOptionColor[] = OPTION_COLOR_KEYS.map((key) => ({
  key,
  label: OPTION_COLOR_LABELS[key],
}))

const resolveColorKey = (key: string): string =>
  isOptionColorKey(key) ? key : DEFAULT_OPTION_COLOR

// Theme-aware CSS-var refs for an option color; resolve per light/dark mode.
// Usable directly in an inline `style` (the stylelint token rule only lints SCSS).
export const getOptionColorBg = (key: string): string =>
  `var(--cf-color-${resolveColorKey(key)}-bg)`

export const getOptionColorText = (key: string): string =>
  `var(--cf-color-${resolveColorKey(key)}-text)`

// The option's solid identity color (same in light/dark) — for swatches and
// legends, where the pill's translucent dark-mode fill would be too faint.
export const getOptionColorSolid = (key: string): string =>
  `var(--cf-color-${resolveColorKey(key)}-solid)`
